if (!customElements.get('social-proof-wall')) {
  customElements.define(
    'social-proof-wall',
    class SocialProofWall extends HTMLElement {
      connectedCallback() {
        this.modal = document.getElementById(this.dataset.modalId);
        if (!this.modal) return;

        this.dialogEl = this.modal.querySelector('.spw-modal__dialog');
        this.mediaSlot = this.modal.querySelector('.spw-modal__media');
        this.infoEl = this.modal.querySelector('.spw-modal__info');
        this.nameEl = this.modal.querySelector('.spw-modal__name');
        this.quoteEl = this.modal.querySelector('.spw-modal__quote');
        this.shopSlot = this.modal.querySelector('.spw-modal__shop');
        this.prevButton = this.modal.querySelector('.spw-modal__nav--prev');
        this.nextButton = this.modal.querySelector('.spw-modal__nav--next');

        this.cards = Array.from(this.querySelectorAll('.spw__card'));
        this.activeIndex = -1;

        this.activeMedia = null;
        this.activeMediaParent = null;
        this.activeMediaNextSibling = null;

        // The grid is a CSS multi-column masonry layout, so revealing more
        // items rebalances the columns and can shuffle cards that were
        // already visible, not just append new ones at the end. The shared
        // show-more-button component only toggles `.hidden` (instant), which
        // reads as the whole wall jumping. Capture positions before that
        // toggle and FLIP-animate every card into its new spot after.
        this.loadMoreButton = this.querySelector('.spw__load-more');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (this.loadMoreButton) {
          this.loadMoreButton.addEventListener('click', () => this.captureCardRects(), true);
          this.loadMoreButton.addEventListener('click', () => this.animateCardRects());
        }

        // The shared ModalDialog component (global.js) closes on any mouse
        // pointerup inside a `.media-modal` that isn't specifically inside
        // `deferred-media`/`product-model` — which would otherwise swallow
        // clicks on the video's native controls, the "shop this look" link,
        // and our own nav/close buttons. Stop it at the dialog boundary so
        // only genuine backdrop clicks dismiss the lightbox.
        this.dialogEl?.addEventListener('pointerup', (event) => event.stopPropagation());

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.prevButton?.addEventListener('click', () => this.step(-1));
        this.nextButton?.addEventListener('click', () => this.step(1));
        document.addEventListener('keydown', this.onModalKeydown.bind(this));
        document.body.addEventListener('modalClosed', this.onModalClosed.bind(this));
      }

      onClick(event) {
        const card = event.target.closest('.spw__card');
        if (!card || !this.contains(card)) return;
        this.openCard(card);
      }

      onKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Only the card itself activates on Enter/Space here — a nested
        // control (e.g. the mute button) is a real <button> and handles its
        // own activation natively.
        if (!event.target.classList.contains('spw__card')) return;
        event.preventDefault();
        this.openCard(event.target);
      }

      onModalKeydown(event) {
        if (!this.modal.hasAttribute('open')) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        }
      }

      step(delta) {
        const nextIndex = this.activeIndex + delta;
        if (nextIndex < 0 || nextIndex >= this.cards.length) return;
        this.restoreActiveMedia();
        this.openCard(this.cards[nextIndex], { keepOpen: true });
      }

      updateNavState() {
        if (this.prevButton) this.prevButton.hidden = this.activeIndex <= 0;
        if (this.nextButton) this.nextButton.hidden = this.activeIndex >= this.cards.length - 1;
      }

      openCard(card, { keepOpen = false } = {}) {
        // Guarantee the clip is attached even if this card hasn't scrolled
        // into the lazy-video primitive's load range yet (e.g. keyboard nav).
        const lazyVideo = card.querySelector('lazy-video');
        if (lazyVideo && typeof lazyVideo.load === 'function') lazyVideo.load();

        const mediaEl = card.querySelector('.spw__video, .spw__img, .spw__placeholder');
        if (!mediaEl) return;

        this.activeIndex = this.cards.indexOf(card);
        this.updateNavState();

        this.activeMedia = mediaEl;
        this.activeMediaParent = mediaEl.parentNode;
        this.activeMediaNextSibling = mediaEl.nextSibling;

        this.mediaSlot.innerHTML = '';
        this.mediaSlot.appendChild(mediaEl);

        if (mediaEl.tagName === 'VIDEO') {
          mediaEl.muted = false;
          mediaEl.controls = true;
        }

        const name = card.dataset.name || '';
        const quote = card.dataset.quote || '';
        this.nameEl.textContent = name;
        this.nameEl.hidden = !name;
        this.quoteEl.textContent = quote;
        this.quoteEl.hidden = !quote;

        this.shopSlot.innerHTML = '';
        const template = card.querySelector('template.spw-shop-template');
        const hasShop = Boolean(template);
        if (hasShop) {
          this.shopSlot.appendChild(template.content.cloneNode(true));
        }
        this.shopSlot.hidden = !hasShop;

        // Nothing to show at all (no caption, no tagged product) — collapse
        // the whole info pill instead of leaving an empty box under the media.
        if (this.infoEl) this.infoEl.hidden = !name && !quote && !hasShop;

        if (!keepOpen) this.modal.show(card);

        if (mediaEl.tagName === 'VIDEO') {
          mediaEl.play().catch(() => {});
        }
      }

      // Moves the currently-active media back to its grid card. Used both
      // when the lightbox actually closes and when stepping to another card
      // while it stays open.
      restoreActiveMedia() {
        if (!this.activeMedia || !this.activeMediaParent) return;

        const mediaEl = this.activeMedia;
        const parent = this.activeMediaParent;
        const nextSibling = this.activeMediaNextSibling;
        this.activeMedia = null;
        this.activeMediaParent = null;
        this.activeMediaNextSibling = null;

        if (mediaEl.tagName === 'VIDEO') {
          mediaEl.pause();
          mediaEl.controls = false;
          mediaEl.muted = true;
        }
        parent.insertBefore(mediaEl, nextSibling);
        if (mediaEl.tagName === 'VIDEO') {
          mediaEl.play().catch(() => {});
          // Re-muting for the grid loop overrides whatever the card's own
          // mute button last said — keep that button's state truthful.
          const muteButton = parent.closest('lazy-video')?.querySelector('.lazy-video__mute');
          if (muteButton) muteButton.setAttribute('aria-pressed', 'true');
        }
      }

      captureCardRects() {
        if (this.reducedMotion.matches) return;
        this.preToggleRects = new Map();
        this.cards.forEach((card) => {
          if (!card.classList.contains('hidden')) {
            this.preToggleRects.set(card, card.getBoundingClientRect());
          }
        });
      }

      animateCardRects() {
        const firstRects = this.preToggleRects;
        this.preToggleRects = null;
        if (!firstRects) return;

        this.cards.forEach((card) => {
          if (card.classList.contains('hidden')) return;
          const first = firstRects.get(card);

          if (!first) {
            // Newly revealed card: fade and rise in rather than pop in place.
            card.animate(
              [
                { opacity: 0, transform: 'translateY(12px)' },
                { opacity: 1, transform: 'translateY(0)' },
              ],
              { duration: 350, easing: 'ease' }
            );
            return;
          }

          const last = card.getBoundingClientRect();
          const deltaX = first.left - last.left;
          const deltaY = first.top - last.top;
          if (!deltaX && !deltaY) return;

          // Already-visible card that the rebalanced columns shifted:
          // animate from its old position to the new one instead of
          // letting it snap there.
          card.animate(
            [{ transform: `translate(${deltaX}px, ${deltaY}px)` }, { transform: 'translate(0, 0)' }],
            { duration: 350, easing: 'cubic-bezier(0.2, 0, 0.2, 1)' }
          );
        });
      }

      onModalClosed() {
        if (!this.activeMedia) return;
        this.activeIndex = -1;
        // Defer past ModalDialog#hide's own pauseAllMedia() call so the
        // restored grid video isn't immediately paused again.
        setTimeout(() => this.restoreActiveMedia(), 0);
      }
    }
  );
}
